import { useState, useCallback, useEffect } from 'react';

// ── Cache 2 lớp: memory + localStorage ──────────────────────────────────────

function lsGet(key: string) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function lsSet(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch {}
}

const memCache = new Map<string, string>();

function cacheGet(postId: string, locale: string, field: string) {
  const key = `trans:${postId}:${locale}:${field}`;
  return memCache.get(key) ?? lsGet(key);
}
function cacheSet(postId: string, locale: string, field: string, value: string) {
  const key = `trans:${postId}:${locale}:${field}`;
  memCache.set(key, value);
  lsSet(key, value);
}

// ── Detect ngôn ngữ nguồn ────────────────────────────────────────────────────

async function detectLang(postId: string, text: string): Promise<string> {
  // Cache detect riêng theo postId — không cần detect lại mỗi lần đổi locale
  const cacheKey = `detect:${postId}`;
  const cached = memCache.get(cacheKey) ?? lsGet(cacheKey);
  if (cached) return cached;

  try {
    const url =
      `https://translate.googleapis.com/translate_a/single` +
      `?client=gtx&sl=auto&tl=en&dt=t&q=${encodeURIComponent(text.slice(0, 100))}`;
    const res = await fetch(url);
    const data = await res.json();
    const lang = data[2] ?? 'vi';
    memCache.set(cacheKey, lang);
    lsSet(cacheKey, lang);
    return lang;
  } catch {
    return 'vi'; // fallback
  }
}

// ── Google Translate ─────────────────────────────────────────────────────────

async function gtranslate(text: string, from: string, to: string): Promise<string> {
  if (!text?.trim() || from === to) return text;

  const url =
    `https://translate.googleapis.com/translate_a/single` +
    `?client=gtx&sl=auto&tl=${to}&dt=t&q=${encodeURIComponent(text)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();

  return (data[0] as any[])?.map((chunk: any) => chunk?.[0] ?? '').join('') ?? text;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

interface TranslateFields {
  title: string;
  description: string | null;
}

interface UseTranslateReturn {
  translatedTitle: string;
  translatedDesc: string | null;
  isTranslating: boolean;
  isTranslated: boolean;
  detectedLang: string | null; // expose để debug hoặc hiển thị badge
}

export function usePostTranslate(
  postId: string,
  original: TranslateFields,
  locale: string,
): UseTranslateReturn {
  const [translatedTitle, setTranslatedTitle] = useState(original.title);
  const [translatedDesc, setTranslatedDesc]   = useState(original.description);
  const [isTranslating, setIsTranslating]     = useState(false);
  const [isTranslated, setIsTranslated]       = useState(false);
  const [detectedLang, setDetectedLang]       = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // 1. Detect ngôn ngữ nguồn
      const sourceLang = await detectLang(postId, original.title);
      if (cancelled) return;
      setDetectedLang(sourceLang);

      // 2. Không cần dịch nếu cùng ngôn ngữ
      if (sourceLang === locale) {
        setTranslatedTitle(original.title);
        setTranslatedDesc(original.description);
        setIsTranslated(false);
        return;
      }

      // 3. Check cache bản dịch
      const cachedTitle = cacheGet(postId, locale, 'title');
      const cachedDesc  = cacheGet(postId, locale, 'desc');
      const descSatisfied = !original.description || cachedDesc != null;

      if (cachedTitle && descSatisfied) {
        setTranslatedTitle(cachedTitle);
        setTranslatedDesc(cachedDesc ?? original.description);
        setIsTranslated(true);
        return;
      }

      // 4. Gọi dịch
      setIsTranslating(true);
      try {
        const jobs = [
          gtranslate(original.title, sourceLang, locale),
          original.description
            ? gtranslate(original.description, sourceLang, locale)
            : Promise.resolve(null),
        ] as const;

        const [title, desc] = await Promise.all(jobs);
        if (cancelled) return;

        setTranslatedTitle(title);
        cacheSet(postId, locale, 'title', title);

        if (original.description) {
          const finalDesc = desc || original.description; // fallback bản gốc nếu dịch rỗng
          setTranslatedDesc(finalDesc);
          cacheSet(postId, locale, 'desc', finalDesc);
        }
        setIsTranslated(true);

      } catch (err) {
        if (!cancelled) console.warn('[usePostTranslate] failed, fallback', err);
      } finally {
        if (!cancelled) setIsTranslating(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [postId, locale]);

  return { translatedTitle, translatedDesc, isTranslating, isTranslated, detectedLang };
}

// ── Google Translate — nhiều q trong 1 request ───────────────────────────────
async function gtranslateMulti(
  texts: string[],
  to: string,
): Promise<string[]> {
  if (!texts.length) return texts;

  const qs = texts.map(t => `&q=${encodeURIComponent(t || ' ')}`).join('');
  const url =
    `https://translate.googleapis.com/translate_a/t` +
    `?client=gtx&sl=auto&tl=${to}&format=text${qs}`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();

  if (texts.length === 1) {
    return [typeof data === 'string' ? data : (Array.isArray(data) ? data[0] : String(data))];
  }
  return data.map((d: any) => (Array.isArray(d) ? d[0] : d));
}

const CHUNK_SIZE = 40; // số title mỗi request, tránh URL quá dài

interface FeedItem { id: string; title: string }

export function useFeedTranslate(
  items: FeedItem[],
  locale: string,
): { titleMap: Map<string, string>; isTranslating: boolean } {
  const [titleMap, setTitleMap] = useState<Map<string, string>>(new Map());
  const [isTranslating, setIsTranslating] = useState(false);

  // Khóa ổn định để tránh chạy lại khi mảng đổi tham chiếu nhưng nội dung như cũ
  const idsKey = items.map(i => i.id).join(',');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // 1. Khởi tạo map từ cache + bản gốc
      const map = new Map<string, string>();
      const need: FeedItem[] = [];

      for (const it of items) {
        const cached = cacheGet(it.id, locale, 'title');
        if (cached) {
          map.set(it.id, cached);
        } else {
          map.set(it.id, it.title); // tạm hiển thị bản gốc
          need.push(it);
        }
      }
      if (!cancelled) setTitleMap(new Map(map));
      if (!need.length) return;

      // 3. Dịch theo chunk
      setIsTranslating(true);
      try {
        for (let i = 0; i < need.length; i += CHUNK_SIZE) {
          const chunk = need.slice(i, i + CHUNK_SIZE);
          const translated = await gtranslateMulti(
            chunk.map(c => c.title),
            locale,
          );
          if (cancelled) return;

          chunk.forEach((c, idx) => {
            const tt = translated[idx] ?? c.title;
            map.set(c.id, tt);
            cacheSet(c.id, locale, 'title', tt);
          });
          setTitleMap(new Map(map)); // cập nhật dần sau mỗi chunk
        }
      } catch (err) {
        if (!cancelled) console.warn('[useFeedTranslate] failed', err);
      } finally {
        if (!cancelled) setIsTranslating(false);
      }
    };

    run();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, locale]);

  return { titleMap, isTranslating };
}

// Export thêm run trigger để dùng trong useEffect
export { type TranslateFields };